/** tm/legal/LegalLawsTab.tsx — 설정 「감시 법령」 탭: 목록·추가(정확한 법령명)·감시 켜기/끄기·삭제(확인) */
import { useEffect, useRef, useState } from 'react';
import { useGuardReport, type SettingsGuard } from './settingsGuard';
import { C, T, R, IMEText } from '../components/ui';
import { getLaws, createLaw, updateLaw, deleteLaw } from './legalApi';
import { btn, inputStyle, errMsg, lawUrl, MUTED } from './legalTheme';
import { Spinner } from './legalUi';
import type { WatchedLaw } from './legalTypes';

interface Props { onToast: (m: string, k?: 'ok' | 'error') => void; guard?: SettingsGuard }

export default function LegalLawsTab({ onToast, guard }: Props) {
  const [rows, setRows] = useState<WatchedLaw[] | null>(null);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [fresh, setFresh] = useState<number | null>(null); // 방금 추가한 줄 (잠깐 강조)
  const rowRefs = useRef(new Map<number, HTMLDivElement>());
  useEffect(() => {
    if (fresh == null) return undefined;
    rowRefs.current.get(fresh)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = setTimeout(() => setFresh(null), 2500);
    return () => clearTimeout(t);
  }, [fresh]);
  useEffect(() => { getLaws().then(setRows).catch(e => setErr(errMsg(e, '법령 목록을 불러오지 못했습니다'))); }, []);

  const add = async (): Promise<boolean> => {
    const n = name.trim();
    if (!n) return true;
    setAdding(true); setErr('');
    try { const r = await createLaw(n); setRows(p => [...(p || []), r]); setName(''); setFresh(r.id); onToast(`「${r.name}」을(를) 감시 법령에 추가했습니다 — 공포·개정되면 새 소식으로 알려 드립니다`); return true; }
    catch (e) {
      const m = errMsg(e, '추가하지 못했습니다');
      setErr(/찾을 수 없|없습니다|404/.test(m) ? `「${n}」을(를) 법제처에서 찾지 못했습니다. 띄어쓰기와 정확한 이름을 확인해 주세요 (예: 중소기업기본법 시행령)` : m);
      return false;
    }
    finally { setAdding(false); }
  };
  useGuardReport(guard, !!name.trim(), add);
  const toggle = async (l: WatchedLaw) => {
    setRows(p => (p || []).map(x => (x.id === l.id ? { ...x, active: !l.active } : x)));
    try { await updateLaw(l.id, { active: !l.active }); }
    catch (e) { setRows(p => (p || []).map(x => (x.id === l.id ? l : x))); onToast(errMsg(e, '바꾸지 못했습니다'), 'error'); }
  };
  const del = async (l: WatchedLaw) => {
    try { await deleteLaw(l.id); setRows(p => (p || []).filter(x => x.id !== l.id)); setConfirmId(null); onToast('감시 법령에서 뺐습니다'); }
    catch (e) { onToast(errMsg(e, '삭제하지 못했습니다'), 'error'); }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <p style={{ margin: '0 0 12px', fontSize: T.sm, color: C.sub, lineHeight: 1.6 }}>
        여기 있는 법령이 공포·개정되면 「법령」 탭과 새 소식에 올라옵니다. 법령명은 법제처에 등록된 이름과 정확히 같아야 합니다 (예: 조세특례제한법 시행령).
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <IMEText value={name} onChange={v => { setName(v); if (err) setErr(''); }} placeholder="법령명 (예: 중소기업기본법 시행령)" aria-label="추가할 법령명"
          onKeyDown={e => { if (!e.nativeEvent.isComposing && e.key === 'Enter') { add(); } }} style={{ ...inputStyle, flex: 1, border: `1px solid ${err ? C.red : C.border}` }} />
        <button type="button" className="lw-f" disabled={adding || !name.trim()} onClick={() => { add(); }} style={{ ...btn('pri'), opacity: adding || !name.trim() ? 0.6 : 1 }}>
          {adding ? <><Spinner /> 확인 중</> : '추가'}
        </button>
      </div>
      {err && <div role="alert" style={{ fontSize: T.sm, color: C.red, marginBottom: 8 }}>{err}</div>}
      {!rows ? <div style={{ color: MUTED, fontSize: T.sm, padding: 12 }}><Spinner /> 불러오는 중…</div> : (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: 'hidden', marginTop: 10 }}>
          {rows.length === 0 && <div style={{ padding: 16, fontSize: T.sm, color: MUTED }}>감시 중인 법령이 없습니다.</div>}
          {rows.map(l => (
            <div key={l.id} ref={el => { if (el) rowRefs.current.set(l.id, el); else rowRefs.current.delete(l.id); }} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', minHeight: 44,
              background: fresh === l.id ? '#ECFDF5' : l.active ? C.white : C.bgAlt, transition: 'background .6s',
            }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 160, cursor: 'pointer', fontSize: T.sm, color: l.active ? C.text : MUTED }}>
                <input type="checkbox" checked={l.active} onChange={() => toggle(l)} aria-label={`${l.name} 감시`} />
                {l.name}
              </label>
              <a href={lawUrl(l.name)} target="_blank" rel="noopener noreferrer" className="lw-f" style={{ fontSize: T.sm, color: C.accent, textDecoration: 'none' }}>현행 원문</a>
              {confirmId === l.id ? (
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: T.sm, color: C.red }}>
                  감시를 멈출까요? 모인 개정 글은 남습니다.
                  <button type="button" className="lw-f" onClick={() => del(l)} style={{ ...btn('danger'), padding: '3px 8px', background: C.red, color: C.white }}>빼기</button>
                  <button type="button" className="lw-f" onClick={() => setConfirmId(null)} style={{ ...btn(), padding: '3px 8px' }}>취소</button>
                </span>
              ) : (
                <button type="button" className="lw-f" onClick={() => setConfirmId(l.id)} aria-label={`${l.name} 삭제`} style={{ ...btn(), padding: '3px 8px', color: MUTED }}>삭제</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
